import { useQuery } from '@apollo/client';
import { IconRemove } from '@newfront-insurance/core-ui';
import { Badge, Button, Card } from '@newfront-insurance/core-ui/v2';
import { useMemo, useState, type ReactNode } from 'react';

import { DeleteTeamDialog } from './delete-team-dialog';
import { GroupAddPeopleDialog } from './group-add-people-dialog';
import { getTeamDetailModel, type TeamSkillRow } from './team-detail-model';
import { TeamManagerDialog } from './team-manager-dialog';
import { TeamSkillsList } from './team-skills-list';
import { useTeamRemovalActions } from './use-team-removal-actions';
import {
  GET_SETTINGS_TEAM_DETAILS,
  type SettingsGroup,
  type SettingsTeamDetails,
} from '../../../../queries/skill-administration';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';
import { SettingsConfirmationDialog } from '../settings-confirmation-dialog';
import { SettingsDetailHeader } from '../settings-detail-header';
import { SettingsPersonRow } from '../settings-person-row';
import { SettingsSectionList } from '../settings-section-list';
import { SettingsActionButton } from '../settings-shared/settings-action-button';

export function TeamSettingsDetail({ team, onBack }: { team: SettingsGroup; onBack: () => void }) {
  const { data, previousData, loading, error, refetch } = useQuery(GET_SETTINGS_TEAM_DETAILS, {
    variables: { groupId: team.uuid },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    context: { queryDeduplication: false },
    notifyOnNetworkStatusChange: true,
  });
  const group = data?.getSkillGroup ?? previousData?.getSkillGroup;
  const invalidGroup = !loading && (group?.uuid !== team.uuid || group.parentGroupUuid === null);
  const actions = useTeamRemovalActions(group?.uuid === team.uuid ? group : undefined, onBack);
  const [deleting, setDeleting] = useState(false);
  const [removing, setRemoving] = useState<
    { kind: 'member'; person: { userUuid: string; name: string } } | { kind: 'skill'; skill: TeamSkillRow } | null
  >(null);
  const confirmRemoval = async () => {
    if (!removing) return;
    const succeeded =
      removing.kind === 'member'
        ? await actions.removePerson(removing.person)
        : await actions.revokeSkill(removing.skill);
    if (succeeded) setRemoving(null);
  };

  return (
    <section aria-label={`Team ${team.name}`} className="flex min-w-0 flex-col gap-5">
      <SettingsDetailHeader
        parentLabel="Teams"
        title={group?.name ?? team.name}
        onBack={onBack}
        actions={
          <SettingsActionButton
            label={`Delete ${team.name}`}
            disabled={!actions.permitted || Boolean(actions.pendingKey) || invalidGroup}
            onClick={() => {
              actions.clearError();
              setDeleting(true);
            }}
          >
            <span aria-hidden="true">
              <IconRemove color="currentColor" size={16} />
            </span>
          </SettingsActionButton>
        }
      />
      {actions.error && !deleting && !removing ? (
        <p role="alert" className="text-fire-500 text-sm">
          {actions.error}
        </p>
      ) : null}
      {loading && !group ? (
        <p role="status" className="text-steel-400 text-sm">
          Loading team…
        </p>
      ) : invalidGroup ? (
        <div role="alert" className="border-steel-75 text-steel-400 rounded-xs border bg-white p-5 text-sm">
          Couldn't load this team.{' '}
          <Button
            type="button"
            variant="inline"
            className="text-sm"
            onClick={() => void refetch().catch(() => undefined)}
          >
            Retry
          </Button>
        </div>
      ) : group ? (
        <>
          {error ? (
            <p role="alert" className="text-steel-400 text-sm">
              Couldn't refresh this team.{' '}
              <Button
                type="button"
                variant="inline"
                className="text-sm"
                onClick={() => void refetch().catch(() => undefined)}
              >
                Retry
              </Button>
            </p>
          ) : null}
          <TeamDetailContent
            group={group}
            onChanged={() => refetch()}
            actions={actions}
            onRemovePerson={(person) => {
              actions.clearError();
              setRemoving({ kind: 'member', person });
            }}
            onRevokeSkill={(skill) => {
              actions.clearError();
              setRemoving({ kind: 'skill', skill });
            }}
          />
        </>
      ) : null}
      {removing && actions.permitted ? (
        <SettingsConfirmationDialog
          title={removing.kind === 'member' ? 'Remove team member' : 'Revoke skill access'}
          description={
            removing.kind === 'member' ? (
              <>
                Remove {removing.person.name} from {group?.name ?? team.name}? Their organization membership and roles
                will stay the same.
              </>
            ) : (
              <>
                Revoke {removing.skill.title} from {group?.name ?? team.name}? This removes the team's access through
                this assignment. The skill and its other shares are kept.
              </>
            )
          }
          confirmLabel={removing.kind === 'member' ? 'Remove member' : 'Revoke access'}
          pendingLabel={removing.kind === 'member' ? 'Removing…' : 'Revoking…'}
          pending={Boolean(actions.pendingKey)}
          error={actions.error}
          onClose={() => setRemoving(null)}
          onConfirm={() => void confirmRemoval()}
        />
      ) : null}
      {deleting && actions.permitted ? (
        <DeleteTeamDialog
          name={group?.name ?? team.name}
          pending={actions.pendingKey === 'delete'}
          error={actions.error}
          onClose={() => setDeleting(false)}
          onConfirm={actions.deleteTeam}
        />
      ) : null}
    </section>
  );
}

function TeamDetailContent({
  group,
  onChanged,
  actions,
  onRemovePerson,
  onRevokeSkill,
}: {
  group: SettingsTeamDetails;
  onChanged: () => Promise<unknown>;
  actions: ReturnType<typeof useTeamRemovalActions>;
  onRemovePerson: (person: { userUuid: string; name: string }) => void;
  onRevokeSkill: (skill: TeamSkillRow) => void;
}) {
  const { settingsAdministration, userUuid } = useChatOverlayContext();
  const [addingAs, setAddingAs] = useState<string | null>(null);
  const [changingManager, setChangingManager] = useState(false);
  const excludedUsers = useMemo(() => new Set([...group.members, ...group.managers]), [group.members, group.managers]);
  const parentAdmins = new Set(settingsAdministration.rootAdmins[group.parentGroupUuid ?? ''] ?? []);
  // The parent organization's managers are its organization admins.
  const parentManagers = new Set(
    settingsAdministration.organizations.find((organization) => organization.uuid === group.parentGroupUuid)
      ?.managers ?? [],
  );
  const getParentRole = (memberUuid: string) => {
    if (parentManagers.has(memberUuid))
      return { label: 'Organization admin', title: 'Manages the parent organization' };
    if (parentAdmins.has(memberUuid)) return { label: 'Admin', title: 'Admin of the parent organization' };
    return null;
  };
  const parentRoleBadge = (memberUuid: string) => {
    const role = getParentRole(memberUuid);
    if (!role) return undefined;
    return (
      <Badge
        variant="neutral"
        className="border-steel-75 text-steel-500 rounded-full border bg-transparent font-normal"
        title={role.title}
      >
        {role.label}
      </Badge>
    );
  };
  const { owners, members, skillCount } = getTeamDetailModel(group);
  // Its manager and the organization's admins can hand the team on. Nova operations use /skills/manage.
  const canChangeManager =
    actions.permitted &&
    !!userUuid &&
    !!group.parentGroupUuid &&
    (group.managers.includes(userUuid) || parentManagers.has(userUuid));
  return (
    <>
      <Card className="border-steel-75 bg-white p-3.5 font-sans shadow-none">
        <dl className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)] items-start gap-x-5 gap-y-5 text-base">
          <TeamDetailField label="Team manager">
            {owners.length ? owners.map((owner) => owner.name).join(', ') : 'No team manager assigned'}
            {canChangeManager ? (
              <>
                {' '}
                <Button
                  type="button"
                  variant="tertiary"
                  className="h-auto p-0 text-sm leading-4"
                  aria-label="Change team manager"
                  disabled={Boolean(actions.pendingKey)}
                  onClick={() => setChangingManager(true)}
                >
                  Change
                </Button>
              </>
            ) : null}
          </TeamDetailField>
          <TeamDetailField label="Members">{members.length}</TeamDetailField>
          <TeamDetailField label="Skills shared to this team">{skillCount}</TeamDetailField>
        </dl>
      </Card>
      <SettingsSectionList
        title={`${members.length} ${members.length === 1 ? 'person' : 'people'}`}
        emptyMessage={!members.length ? 'No people in this team yet.' : undefined}
        headerEnd={
          <Button
            type="button"
            variant="tertiary"
            className="h-auto p-0 text-sm leading-4"
            disabled={!userUuid || Boolean(actions.pendingKey)}
            onClick={() => {
              if (userUuid) setAddingAs(userUuid);
            }}
          >
            + Add people
          </Button>
        }
      >
        {members.length
          ? members.map((member) => (
              <SettingsPersonRow
                key={member.userUuid}
                name={member.name}
                imageUrl={member.imageUrl}
                initials={member.initials}
                badges={parentRoleBadge(member.userUuid)}
                action={
                  <Button
                    type="button"
                    variant="transparent"
                    className="text-wtw-coralreef-500 h-auto p-0 text-sm leading-4"
                    aria-label={`Remove ${member.name} from team`}
                    disabled={!actions.permitted || Boolean(actions.pendingKey)}
                    aria-busy={actions.pendingKey === `member:${member.userUuid}`}
                    onClick={() => onRemovePerson(member)}
                  >
                    {actions.pendingKey === `member:${member.userUuid}` ? 'Removing…' : 'Remove from team'}
                  </Button>
                }
              />
            ))
          : null}
      </SettingsSectionList>
      <TeamSkillsList
        group={group}
        onRevoke={onRevokeSkill}
        disabled={!actions.permitted || Boolean(actions.pendingKey)}
        pendingKey={actions.pendingKey}
      />
      {addingAs && addingAs === userUuid ? (
        <GroupAddPeopleDialog
          key={`${userUuid}:${group.uuid}`}
          kind="members"
          groupId={group.uuid}
          parentGroupUuid={group.parentGroupUuid}
          excludedUserUuids={excludedUsers}
          onClose={() => setAddingAs(null)}
          onChanged={onChanged}
        />
      ) : null}
      {changingManager && group.parentGroupUuid ? (
        <TeamManagerDialog
          team={{ ...group, parentGroupUuid: group.parentGroupUuid }}
          currentManagerName={owners.length === 1 ? owners[0].name : undefined}
          onClose={() => setChangingManager(false)}
          onChanged={() => {
            // Handing the team on can end this person's access to manage it.
            settingsAdministration.retry();
            void onChanged().catch(() => undefined);
          }}
        />
      ) : null}
    </>
  );
}

function TeamDetailField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-steel-500 text-sm leading-5 font-semibold">{label}</dt>
      <dd className="text-steel-400 min-w-0 leading-5 break-words">{children}</dd>
    </>
  );
}
